import { wupoServerUri } from '../Api';

/**
 * Thin client for the conversational NL2SQL pipeline (backend ChatController
 * at /api/auth/chat). HITL approve/answer live on the shared report HITL
 * surface — `ReportPipelineRunner.resumeFromCheckpointAsync` dispatches CHAT
 * checkpoints to the chat runner, so one set of endpoints serves both pipelines.
 */

const jsonHeaders = (jwtToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${jwtToken}`,
});

const safeJson = async (response) => {
  if (!response || !response.ok) return null;
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

// ── Conversation-control read mappers (Phase 7) ──────────────────────────────
// Thin, defensive accessors over the chat turn shape so the workspace doesn't
// re-derive classification semantics inline. No contract break: turn_kind is an
// additive read-only field on ChatTurnDetailDto and is treated as NEW_QUESTION
// when absent (legacy turns predating Phase 6 classification).

export const CHAT_TURN_KINDS = ['NEW_QUESTION', 'FOLLOW_UP', 'REFINEMENT', 'CORRECTION', 'META'];

/** Normalize a turn's classification; unknown/blank/null → 'NEW_QUESTION'. */
export const readTurnKind = (turn) => {
  const raw = turn && turn.turnKind;
  if (!raw || typeof raw !== 'string') return 'NEW_QUESTION';
  const norm = raw.trim().toUpperCase().replace(/[-\s]/g, '_');
  return CHAT_TURN_KINDS.includes(norm) ? norm : 'NEW_QUESTION';
};

/**
 * True when a turn builds on prior turns (FOLLOW_UP / REFINEMENT / CORRECTION),
 * or when framing flagged it as a follow-up even if classification is absent —
 * mirrors the backend's `reusesPriorContext()` predicate plus the legacy
 * is_follow_up signal so older turns still render linkage.
 */
export const reusesPriorContext = (turn) => {
  const kind = readTurnKind(turn);
  if (kind === 'FOLLOW_UP' || kind === 'REFINEMENT' || kind === 'CORRECTION') return true;
  return isFollowUpFraming(turn);
};

/** Parse the framing JSON of a turn, returning null on any malformed payload. */
export const parseFraming = (turn) => {
  const raw = turn && turn.framingJson;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/** Legacy follow-up signal read from the framing payload's is_follow_up flag. */
export const isFollowUpFraming = (turn) => {
  const framing = parseFraming(turn);
  return !!(framing && framing.is_follow_up);
};

/**
 * True when a turn is *about* the conversation so far and should render a linkage
 * affordance — the prior-context kinds plus META (which references an earlier
 * answer without re-running the pipeline). Superset of {@link reusesPriorContext}.
 */
export const buildsOnConversation = (turn) => reusesPriorContext(turn) || readTurnKind(turn) === 'META';

/** Free-text note describing what prior context this turn carried over, if any. */
export const followUpContext = (turn) => {
  const framing = parseFraming(turn);
  const note = framing && framing.follow_up_context;
  return note && String(note).trim() ? String(note).trim() : null;
};

/** Human-readable short label for a non-default turn kind (null for NEW_QUESTION). */
export const turnKindLabel = (turn) => {
  switch (readTurnKind(turn)) {
    case 'FOLLOW_UP':
      return 'Follow-up';
    case 'REFINEMENT':
      return 'Refinement';
    case 'CORRECTION':
      return 'Correction';
    case 'META':
      return 'About a previous answer';
    default:
      return null;
  }
};

export const createChatSession = async (title, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/sessions`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify({ title: title || null }),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('createChatSession error:', e);
    return null;
  }
};

export const listChatSessions = async (jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/sessions`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('listChatSessions error:', e);
    return null;
  }
};

export const getChatSession = async (sessionId, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/sessions/${sessionId}`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('getChatSession error:', e);
    return null;
  }
};

export const askQuestion = async (sessionId, question, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/sessions/${sessionId}/ask`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify({ question }),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('askQuestion error:', e);
    return null;
  }
};

export const getTurn = async (turnId, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/turns/${turnId}`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('getTurn error:', e);
    return null;
  }
};

export const getTurnPipelineStatus = async (turnId, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/chat/turns/${turnId}/pipeline-status`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('getTurnPipelineStatus error:', e);
    return null;
  }
};

// HITL — shared with the report pipeline (single surface, dispatched by pipelineKind backend-side).
export const approveCheckpoint = async (checkpointId, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/reports/pipeline-approve/${checkpointId}`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('approveCheckpoint error:', e);
    return null;
  }
};

export const submitAnswer = async (checkpointId, answer, jwtToken) => {
  try {
    const response = await fetch(`${wupoServerUri.devnet}/api/auth/reports/pipeline-answer/${checkpointId}`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify({ answer }),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('submitAnswer error:', e);
    return null;
  }
};
